var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Big from "big.js";
import { decodeFixedPointType, newMonetaryAmount, parseSystemVault, newCurrencyId, newVaultId, newVaultCurrencyPair, addHexPrefix, currencyIdToMonetaryCurrency, decodeRpcVaultId, addressOrPairAsAccountId, } from "../utils";
import { VaultExt, VaultStatusExt, } from "../types";
import { DefaultNominationAPI } from "./index";
export const NO_LIQUIDATION_VAULT_FOUND_REJECTION = "No liquidation vault found";
export class DefaultVaultsAPI {
    constructor(api, electrsAPI, wrappedCurrency, governanceCurrency, tokensAPI, oracleAPI, feeAPI, rewardsAPI, systemAPI, transactionAPI) {
        this.api = api;
        this.electrsAPI = electrsAPI;
        this.wrappedCurrency = wrappedCurrency;
        this.governanceCurrency = governanceCurrency;
        this.tokensAPI = tokensAPI;
        this.oracleAPI = oracleAPI;
        this.feeAPI = feeAPI;
        this.rewardsAPI = rewardsAPI;
        this.systemAPI = systemAPI;
        this.transactionAPI = transactionAPI;
    }
    getWrappedCurrency() {
        return this.wrappedCurrency;
    }
    registerNewCollateralVault(collateralAmount) {
        // check the vault account is set
        const vaultAccount = this.transactionAPI.getAccount();
        if (vaultAccount === undefined) {
            throw new Error("Failed to read account in vaults API; account must be set in interbtc API");
        }
        const extrinsic = this.buildRegisterVaultExtrinsic(collateralAmount);
        const registerVaultEvent = this.getRegisterVaultEvent();
        return { extrinsic, event: registerVaultEvent };
    }
    // helper method; mainly for easier mocking in unit tests without an active ApiPromise instance
    getRegisterVaultEvent() {
        return this.api.events.vaultRegistry.RegisterVault;
    }
    buildRegisterVaultExtrinsic(collateralAmount) {
        const currencyPair = newVaultCurrencyPair(this.api, collateralAmount.currency, this.getWrappedCurrency());
        const amountAtomicUnit = this.api.createType("Balance", collateralAmount.toString(true));
        return this.api.tx.vaultRegistry.registerVault(currencyPair, amountAtomicUnit);
    }
    buildRegisterPublicKeyExtrinsic(publicKey) {
        return this.api.tx.vaultRegistry.registerPublicKey(publicKey);
    }
    buildWithdrawCollateralExtrinsic(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const account = this.transactionAPI.getAccount();
            if (account == undefined) {
                throw new Error("Account must be connected to create a collateral withdrawal request.");
            }
            const vaultAccountId = addressOrPairAsAccountId(this.api, account);
            return yield DefaultNominationAPI.buildWithdrawCollateralExtrinsic(this.api, this.rewardsAPI, vaultAccountId, amount, this.wrappedCurrency);
        });
    }
    withdrawCollateral(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = yield this.buildWithdrawCollateralExtrinsic(amount);
            return { extrinsic: tx, event: this.api.events.vaultRegistry.WithdrawCollateral };
        });
    }
    buildDepositCollateralExtrinsic(amount) {
        const account = this.transactionAPI.getAccount();
        if (account == undefined) {
            throw new Error("Account must be connected to create a collateral deposit request.");
        }
        const vaultAccountId = addressOrPairAsAccountId(this.api, account);
        return DefaultNominationAPI.buildDepositCollateralExtrinsic(this.api, vaultAccountId, amount, this.wrappedCurrency);
    }
    depositCollateral(amount) {
        const tx = this.buildDepositCollateralExtrinsic(amount);
        return { extrinsic: tx, event: this.api.events.vaultRegistry.DepositCollateral };
    }
    list(atBlock) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultsMap = yield (atBlock
                ? (yield this.api.at(atBlock)).query.vaultRegistry.vaults.entries()
                : this.api.query.vaultRegistry.vaults.entries());
            return Promise.all(vaultsMap.filter((v) => v[1].isSome).map((v) => this.parseVault(v[1].value)));
        });
    }
    getOrNull(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const vaultId = newVaultId(this.api, vaultAccountId.toString(), collateralCurrency, this.wrappedCurrency);
                const vault = yield this.api.query.vaultRegistry.vaults(vaultId);
                if (!vault.isSome) {
                    return null;
                }
                return this.parseVault(vault.value);
            }
            catch (error) {
                return Promise.reject(error);
            }
        });
    }
    get(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vault = yield this.getOrNull(vaultAccountId, collateralCurrency);
            if (vault === null) {
                return Promise.reject(new Error(`Vault does not exist for id '${vaultAccountId}' and collateral '${collateralCurrency.name}'`));
            }
            return vault;
        });
    }
    getCollateral(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.rewardsAPI.computeCollateralInStakingPool(newVaultId(this.api, vaultAccountId.toString(), collateralCurrency, this.wrappedCurrency), vaultAccountId);
        });
    }
    getMinimumCollateral(collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const collateralCurrencyId = newCurrencyId(this.api, collateralCurrency);
            const minimumCollateral = yield this.api.query.vaultRegistry.minimumCollateralVault(collateralCurrencyId);
            return newMonetaryAmount(minimumCollateral.toString(), collateralCurrency);
        });
    }
    getMaxNominationRatio(collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const [premiumRedeemThreshold, secureCollateralThreshold] = yield Promise.all([
                this.getPremiumRedeemThreshold(collateralCurrency),
                this.getSecureCollateralThreshold(collateralCurrency),
            ]);
            return secureCollateralThreshold.div(premiumRedeemThreshold);
        });
    }
    computeBackingCollateral(vaultId, nonce) {
        return __awaiter(this, void 0, void 0, function* () {
            const collateralCurrency = yield currencyIdToMonetaryCurrency(this.api, vaultId.currencies.collateral);
            if (nonce === undefined) {
                nonce = yield this.rewardsAPI.getStakingPoolNonce(collateralCurrency, vaultId.accountId);
            }
            const rawBackingCollateral = yield this.api.query.vaultStaking.totalCurrentStake(nonce, vaultId);
            return newMonetaryAmount(decodeFixedPointType(rawBackingCollateral), collateralCurrency);
        });
    }
    backingCollateralProportion(vaultAccountId, nominatorId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vault = yield this.get(vaultAccountId, collateralCurrency);
            const nominatorCollateral = yield this.rewardsAPI.computeCollateralInStakingPool(newVaultId(this.api, vaultAccountId.toString(), collateralCurrency, this.wrappedCurrency), nominatorId);
            // short-circuit a potential 0 div 0 scenario where
            // the nominator is equal to the vault and has zero collateral
            if (nominatorCollateral.isZero()) {
                return nominatorCollateral.toBig();
            }
            const backingCollateral = vault.backingCollateral.toBig();
            if (backingCollateral.eq(0)) {
                return Promise.reject(new Error("No backing collateral"));
            }
            return nominatorCollateral.toBig().div(backingCollateral);
        });
    }
    getBlockRewardAPY(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultCurrencyPair = newVaultCurrencyPair(this.api, collateralCurrency, this.wrappedCurrency);
            const vaultIdParam = {
                account_id: vaultAccountId,
                currencies: vaultCurrencyPair,
            };
            // get estimated annual rewards as rate (ie. not percent)
            const rawRewardRate = yield this.api.rpc.reward.estimateVaultRewardRate(vaultIdParam);
            const annualRewardRate = decodeFixedPointType(rawRewardRate);
            return annualRewardRate.mul(100);
        });
    }
    computeReward(vaultAccountId, collateralCurrency, rewardCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultCurrencyPair = newVaultCurrencyPair(this.api, collateralCurrency, this.wrappedCurrency);
            const params = {
                account_id: vaultAccountId,
                currencies: vaultCurrencyPair,
            };
            const reward = yield this.api.rpc.reward.computeVaultReward(params, newCurrencyId(this.api, rewardCurrency));
            return newMonetaryAmount(reward.amount.toString(), rewardCurrency);
        });
    }
    getWrappedReward(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.computeReward(vaultAccountId, collateralCurrency, this.wrappedCurrency);
        });
    }
    getGovernanceReward(vaultAccountId, vaultCollateral, governanceCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.computeReward(vaultAccountId, vaultCollateral, governanceCurrency);
        });
    }
    getStakingCapacity(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vault = yield this.get(vaultAccountId, collateralCurrency);
            const [collateral, maxNominationRatio] = yield Promise.all([
                this.getCollateral(vaultAccountId, collateralCurrency),
                this.getMaxNominationRatio(yield currencyIdToMonetaryCurrency(this.api, vault.id.currencies.collateral)),
            ]);
            return collateral.mul(maxNominationRatio).sub(vault.backingCollateral);
        });
    }
    getLiquidationVault(collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultCurrencyPair = newVaultCurrencyPair(this.api, collateralCurrency, this.wrappedCurrency);
            const liquidationVault = yield this.api.query.vaultRegistry.liquidationVault(vaultCurrencyPair);
            if (!liquidationVault.isSome) {
                return Promise.reject(NO_LIQUIDATION_VAULT_FOUND_REJECTION);
            }
            return yield parseSystemVault(this.api, liquidationVault.value, this.wrappedCurrency, collateralCurrency);
        });
    }
    isNoTokensIssuedError(e) {
        return e !== undefined && e.includes("No tokens issued");
    }
    isBelowPremiumThreshold(vaultId) {
        return __awaiter(this, void 0, void 0, function* () {
            const [premiumRedeemThreshold, vaultCollateralization] = yield Promise.all([
                this.getPremiumRedeemThreshold(yield currencyIdToMonetaryCurrency(this.api, vaultId.currencies.collateral)),
                this.getCollateralizationFromVault(vaultId),
            ]);
            return vaultCollateralization.lt(premiumRedeemThreshold);
        });
    }
    getVaultCollateralization(vaultAccountId, collateralCurrency, newCollateral, onlyIssued = false) {
        return __awaiter(this, void 0, void 0, function* () {
            let collateralization = undefined;
            const vaultId = newVaultId(this.api, vaultAccountId.toString(), collateralCurrency, this.wrappedCurrency);
            try {
                if (newCollateral) {
                    collateralization = yield this.getCollateralizationFromVaultAndCollateral(vaultId, newCollateral, onlyIssued);
                }
                else {
                    collateralization = yield this.getCollateralizationFromVault(vaultId, onlyIssued);
                }
            }
            catch (e) {
                if (this.isNoTokensIssuedError(e)) {
                    return Promise.resolve(undefined);
                }
                return Promise.reject(new Error(`Error during collateralization computation: ${e}`));
            }
            if (!collateralization) {
                return Promise.resolve(undefined);
            }
            return collateralization;
        });
    }
    getCollateralizationFromVault(vaultId, onlyIssued = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const collateral = yield this.computeBackingCollateral(vaultId);
            return this.getCollateralizationFromVaultAndCollateral(vaultId, collateral, onlyIssued);
        });
    }
    getCollateralizationFromVaultAndCollateral(vaultId, newCollateral, onlyIssued) {
        return __awaiter(this, void 0, void 0, function* () {
            const vault = yield this.get(vaultId.accountId, yield currencyIdToMonetaryCurrency(this.api, vaultId.currencies.collateral));
            const issuedTokens = yield (onlyIssued ? Promise.resolve(vault.issuedTokens) : vault.getBackedTokens());
            if (issuedTokens.isZero()) {
                return Promise.reject("No tokens issued");
            }
            const collateralInWrapped = yield this.oracleAPI.convertCollateralToWrapped(newCollateral);
            return collateralInWrapped.toBig().div(issuedTokens.toBig());
        });
    }
    getSystemCollateralization() {
        return __awaiter(this, void 0, void 0, function* () {
            // TODO: Implement once method of calculation is decided on
            return Promise.resolve(undefined);
        });
    }
    getRequiredCollateralForVault(vaultAccountId, currency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vault = yield this.get(vaultAccountId, currency);
            const issuedTokens = vault.getBackedTokens();
            return yield this.getRequiredCollateralForWrapped(issuedTokens, currency);
        });
    }
    getRequiredCollateralForWrapped(wrappedAmount, currency) {
        return __awaiter(this, void 0, void 0, function* () {
            const secureCollateralThreshold = yield this.getSecureCollateralThreshold(currency);
            const requiredCollateralInWrappedCurrency = wrappedAmount.mul(secureCollateralThreshold);
            return yield this.oracleAPI.convertWrappedToCurrency(requiredCollateralInWrappedCurrency, currency);
        });
    }
    getIssuedAmount(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vault = yield this.get(vaultAccountId, collateralCurrency);
            return vault.issuedTokens;
        });
    }
    getTotalIssuedAmount() {
        return __awaiter(this, void 0, void 0, function* () {
            const issuedTokens = yield this.tokensAPI.total(this.wrappedCurrency);
            return issuedTokens;
        });
    }
    getTotalIssuableAmount() {
        return __awaiter(this, void 0, void 0, function* () {
            const issuableVaults = yield this.api.rpc.vaultRegistry.getVaultsWithIssuableTokens();
            const totalBalanceAtomic = issuableVaults.reduce((acc, [_, balanceWrapper]) => acc.add(balanceWrapper.amount.toString()), new Big(0));
            const wrappedCurrency = this.getWrappedCurrency();
            return newMonetaryAmount(totalBalanceAtomic, wrappedCurrency);
        });
    }
    calculateCapacity(collateral) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const [exchangeRate, secureCollateralThreshold] = yield Promise.all([
                    this.oracleAPI.getExchangeRate(collateral.currency),
                    this.getSecureCollateralThreshold(collateral.currency),
                ]);
                const unusedCollateral = collateral.div(secureCollateralThreshold);
                return exchangeRate.toBase(unusedCollateral);
            }
            catch (error) {
                return newMonetaryAmount(0, this.wrappedCurrency);
            }
        });
    }
    getIssuableTokensFromVault(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultId = newVaultId(this.api, vaultAccountId.toString(), collateralCurrency, this.getWrappedCurrency());
            const balance = yield this.api.rpc.vaultRegistry.getIssueableTokensFromVault({
                account_id: vaultId.accountId,
                currencies: vaultId.currencies,
            });
            const wrappedCurrencyPrimitive = newCurrencyId(this.api, this.getWrappedCurrency());
            const currency = yield currencyIdToMonetaryCurrency(this.api, wrappedCurrencyPrimitive);
            const amount = newMonetaryAmount(balance.amount.toString(), currency);
            return amount;
        });
    }
    selectRandomVaultIssue(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaults = yield this.getVaultsWithIssuableTokens();
            for (const [id, issuableAmount] of vaults) {
                if (issuableAmount.gte(amount)) {
                    return id;
                }
            }
            return Promise.reject(new Error("Did not find vault with sufficient collateral"));
        });
    }
    selectRandomVaultRedeem(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            // Selects the first vault with sufficient tokens
            const vaults = yield this.list();
            for (const vault of vaults) {
                if (vault.issuedTokens.gte(amount)) {
                    return vault.id;
                }
            }
            return Promise.reject(new Error("Did not find vault with sufficient locked BTC"));
        });
    }
    getPremiumRedeemVaults() {
        return __awaiter(this, void 0, void 0, function* () {
            const map = new Map();
            const premiumRedeemVaults = yield this.api.rpc.vaultRegistry.getPremiumRedeemVaults();
            for (const [vaultId, balanceWrapper] of premiumRedeemVaults) {
                const amount = newMonetaryAmount(balanceWrapper.amount.toString(), this.getWrappedCurrency());
                const ibtcPrimitivesVaultId = yield decodeRpcVaultId(this.api, vaultId);
                map.set(ibtcPrimitivesVaultId, amount);
            }
            return map;
        });
    }
    getVaultsWithIssuableTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const issuableVaults = yield this.api.rpc.vaultRegistry.getVaultsWithIssuableTokens();
            const vaultIdsToAmountsMap = new Map();
            for (const [vaultId, balanceWrapper] of issuableVaults) {
                const amount = newMonetaryAmount(balanceWrapper.amount.toString(), this.getWrappedCurrency());
                const ibtcPrimitivesVaultId = yield decodeRpcVaultId(this.api, vaultId);
                vaultIdsToAmountsMap.set(ibtcPrimitivesVaultId, amount);
            }
            return vaultIdsToAmountsMap;
        });
    }
    isVaultEligibleForRedeem(vault, activeBlockNumber) {
        const bannedUntilBlockNumber = vault.bannedUntil || 0;
        return ((vault.status === VaultStatusExt.Active || vault.status === VaultStatusExt.Inactive) &&
            vault.issuedTokens.gt(vault.toBeRedeemedTokens) &&
            bannedUntilBlockNumber < activeBlockNumber);
    }
    getVaultsEligibleForRedeeming() {
        return __awaiter(this, void 0, void 0, function* () {
            const [vaults, activeBlockNumber] = yield Promise.all([
                this.list(),
                this.systemAPI.getCurrentActiveBlockNumber(),
            ]);
            // only non-banned, non-liquidated vaults with liquidity are eligible for redeems
            const redeemVaults = vaults.filter((vault) => {
                return this.isVaultEligibleForRedeem(vault, activeBlockNumber);
            });
            return redeemVaults;
        });
    }
    getVaultsWithRedeemableTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const map = new Map();
            const vaults = yield this.getVaultsEligibleForRedeeming();
            vaults
                .sort((vault1, vault2) => {
                // Descending order
                const vault1Redeemable = vault1.getRedeemableTokens().toBig();
                const vault2Redeemable = vault2.getRedeemableTokens().toBig();
                return vault2Redeemable.sub(vault1Redeemable).toNumber();
            })
                .forEach((vault) => map.set(vault.id, vault.getRedeemableTokens()));
            return map;
        });
    }
    isVaultFlaggedForTheft(vaultId, btcTxId) {
        return __awaiter(this, void 0, void 0, function* () {
            const theftReports = yield this.api.query.relay.theftReports(vaultId, {
                content: addHexPrefix(btcTxId),
            });
            return theftReports.isEmpty;
        });
    }
    getLiquidationCollateralThreshold(collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultCurrencyPair = newVaultCurrencyPair(this.api, collateralCurrency, this.wrappedCurrency);
            const threshold = yield this.api.query.vaultRegistry.liquidationCollateralThreshold(vaultCurrencyPair);
            if (!threshold.isSome) {
                return Promise.reject(`No liquidation threshold for currency ${collateralCurrency.ticker}`);
            }
            return decodeFixedPointType(threshold.value);
        });
    }
    getPremiumRedeemThreshold(collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultCurrencyPair = newVaultCurrencyPair(this.api, collateralCurrency, this.wrappedCurrency);
            const threshold = yield this.api.query.vaultRegistry.premiumRedeemThreshold(vaultCurrencyPair);
            if (!threshold.isSome) {
                return Promise.reject(`No premium redeem threshold for currency ${collateralCurrency.ticker}`);
            }
            return decodeFixedPointType(threshold.value);
        });
    }
    getSecureCollateralThreshold(collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultCurrencyPair = newVaultCurrencyPair(this.api, collateralCurrency, this.wrappedCurrency);
            const threshold = yield this.api.query.vaultRegistry.secureCollateralThreshold(vaultCurrencyPair);
            return decodeFixedPointType(threshold.value);
        });
    }
    getAPY(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const [feesWrapped, lockedCollateral, blockRewardsAPY] = yield Promise.all([
                this.getWrappedReward(vaultAccountId, collateralCurrency),
                this.getCollateral(vaultAccountId, collateralCurrency),
                this.getBlockRewardAPY(vaultAccountId, collateralCurrency),
            ]);
            return (yield this.feeAPI.calculateAPY(feesWrapped, lockedCollateral)).add(blockRewardsAPY);
        });
    }
    getPunishmentFee() {
        return __awaiter(this, void 0, void 0, function* () {
            const fee = yield this.api.query.fee.punishmentFee();
            return decodeFixedPointType(fee);
        });
    }
    parseVaultStatus(status) {
        if (status.isActive) {
            return status.asActive.isTrue ? VaultStatusExt.Active : VaultStatusExt.Inactive;
        }
        else if (status.isLiquidated) {
            return VaultStatusExt.Liquidated;
        }
        else {
            throw new Error("Unknown vault status");
        }
    }
    parseVault(vault) {
        return __awaiter(this, void 0, void 0, function* () {
            const collateralCurrency = yield currencyIdToMonetaryCurrency(this.api, vault.id.currencies.collateral);
            const replaceCollateral = newMonetaryAmount(vault.replaceCollateral.toString(), collateralCurrency);
            const liquidatedCollateral = newMonetaryAmount(vault.liquidatedCollateral.toString(), collateralCurrency);
            const backingCollateral = yield this.computeBackingCollateral(vault.id);
            const secureThreshold = vault.secureCollateralThreshold.isSome
                ? decodeFixedPointType(vault.secureCollateralThreshold.unwrap())
                : yield this.getSecureCollateralThreshold(collateralCurrency);
            return new VaultExt(this.api, this.oracleAPI, this.systemAPI, backingCollateral, vault.id, this.parseVaultStatus(vault.status), vault.bannedUntil.isSome ? vault.bannedUntil.value.toNumber() : undefined, newMonetaryAmount(vault.toBeIssuedTokens.toString(), this.wrappedCurrency), newMonetaryAmount(vault.issuedTokens.toString(), this.wrappedCurrency), newMonetaryAmount(vault.toBeRedeemedTokens.toString(), this.wrappedCurrency), newMonetaryAmount(vault.toBeReplacedTokens.toString(), this.wrappedCurrency), replaceCollateral, liquidatedCollateral, secureThreshold);
        });
    }
    buildAcceptNewIssuesExtrinsic(collateralCurrency, acceptNewIssues) {
        const vaultCurrencyPair = newVaultCurrencyPair(this.api, collateralCurrency, this.wrappedCurrency);
        return this.api.tx.vaultRegistry.acceptNewIssues(vaultCurrencyPair, acceptNewIssues);
    }
    toggleIssueRequests(vaultId, acceptNewIssues) {
        return __awaiter(this, void 0, void 0, function* () {
            const collateralCurrency = yield currencyIdToMonetaryCurrency(this.api, vaultId.currencies.collateral);
            const tx = this.buildAcceptNewIssuesExtrinsic(collateralCurrency, acceptNewIssues);
            return { extrinsic: tx, event: this.api.events.system.ExtrinsicSuccess };
        });
    }
    getExchangeRateForLiquidation(vaultAccountId, collateralCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const [vaultExt, liquidationRateThreshold, lockedCollateral] = yield Promise.all([
                this.get(vaultAccountId, collateralCurrency),
                this.getLiquidationCollateralThreshold(collateralCurrency),
                this.getCollateral(vaultAccountId, collateralCurrency),
            ]);
            if (liquidationRateThreshold.eq(0)) {
                return undefined;
            }
            const issuedTokens = vaultExt.getBackedTokens();
            if (issuedTokens.toBig().eq(0)) {
                return undefined;
            }
            // calculate the theoretical exchange rate at which the vault would be (close to) liquidated
            const liquidationRateCollateralPerWrapped = lockedCollateral
                .toBig()
                .div(issuedTokens.toBig().mul(liquidationRateThreshold));
            return liquidationRateCollateralPerWrapped;
        });
    }
}
//# sourceMappingURL=vaults.js.map